/**
 * Single source of truth for the Urgent Medical Care city directory.
 *
 *   To launch a new city:
 *     1. Build the dedicated landing page (see UrgentMedicalCareHoiAnPage.jsx
 *        as the reference template — it is feature-complete and SEO-tagged).
 *     2. Wire its route in `src/App.js` under LocalizedRoutes:
 *          <Route path="/urgent-medical-care-<slug>" element={<PageX />} />
 *     3. Add it to `frontend/scripts/generate-sitemap.mjs` STATIC_ROUTES.
 *     4. Flip `status: 'live'` and `href: '/urgent-medical-care-<slug>'` here.
 *     5. That's it — the homepage section and any other consumers update
 *        automatically.
 *
 * Status values:
 *   • 'live'         — page exists and is reachable, renders primary CTA.
 *   • 'coming-soon'  — placeholder card, renders muted "Notify me" CTA.
 */

export const URGENT_CARE_CITIES = [
  {
    slug: 'hoi-an',
    name: 'Da Nang & Hoi An',
    region: 'Central Coast',
    status: 'live',
    href: '/urgent-medical-care-hoi-an',
    coverage: 'Coverage across Da Nang & Hoi An',
    blurb: '24/7 doctor call-outs across Da Nang & Hoi An — a licensed '
      + 'English-speaking doctor arrives at your hotel, apartment or Airbnb '
      + 'within 60 minutes for consultations, IV therapy and wound dressing.',
    image: 'https://images.pexels.com/photos/7676441/pexels-photo-7676441.jpeg?auto=compress&cs=tinysrgb&w=1400',
    fallback: 'https://images.pexels.com/photos/4350230/pexels-photo-4350230.jpeg?auto=compress&cs=tinysrgb&w=1400',
    eyebrow: 'Available 24/7',
    trustPoints: [
      'Doctor arrives within 60 minutes',
      'English-speaking doctors',
      'IV therapy & wound dressing',
      'Transparent quotes before any visit',
    ],
    fromPrice: 'From USD 29',
  },
  {
    slug: 'ho-chi-minh',
    name: 'Ho Chi Minh City',
    region: 'South',
    status: 'coming-soon',
    href: '/urgent-medical-care-ho-chi-minh',
    blurb: '24/7 doctor call-outs across District 1, District 3, Thao Dien and '
      + 'District 7 — a doctor to your hotel, apartment or Airbnb within 60 minutes.',
    image: 'https://images.pexels.com/photos/1659437/pexels-photo-1659437.jpeg?auto=compress&cs=tinysrgb&w=900',
    fallback: 'https://images.pexels.com/photos/248159/pexels-photo-248159.jpeg?auto=compress&cs=tinysrgb&w=900',
    eyebrow: 'Available 24/7',
  },
  {
    slug: 'hanoi',
    name: 'Hanoi',
    region: 'North',
    status: 'coming-soon',
    href: '/urgent-medical-care-hanoi',
    blurb: '24/7 doctor call-outs in Hanoi\'s Old Quarter, Ba Dinh and Tay Ho — '
      + 'a doctor to your hotel, apartment or Airbnb within 60 minutes.',
    image: 'https://images.pexels.com/photos/2412602/pexels-photo-2412602.jpeg?auto=compress&cs=tinysrgb&w=900',
    fallback: 'https://images.pexels.com/photos/2901209/pexels-photo-2901209.jpeg?auto=compress&cs=tinysrgb&w=900',
    eyebrow: 'Available 24/7',
  },
  {
    slug: 'nha-trang',
    name: 'Nha Trang',
    region: 'South Central Coast',
    status: 'coming-soon',
    href: '/urgent-medical-care-nha-trang',
    blurb: '24/7 doctor call-outs along the Tran Phu beachfront — IV rehydration, '
      + 'wound dressing and hotel doctor visits within 60 minutes.',
    image: 'https://images.pexels.com/photos/2603464/pexels-photo-2603464.jpeg?auto=compress&cs=tinysrgb&w=900',
    fallback: 'https://images.pexels.com/photos/3408744/pexels-photo-3408744.jpeg?auto=compress&cs=tinysrgb&w=900',
    eyebrow: 'Available 24/7',
  },
  {
    slug: 'phu-quoc',
    name: 'Phu Quoc',
    region: 'Island',
    status: 'coming-soon',
    href: '/urgent-medical-care-phu-quoc',
    blurb: '24/7 doctor call-outs on Phu Quoc — hotel, resort and Airbnb doctor '
      + 'visits, IV therapy and English-speaking consultations within 60 minutes.',
    image: 'https://images.pexels.com/photos/1450353/pexels-photo-1450353.jpeg?auto=compress&cs=tinysrgb&w=900',
    fallback: 'https://images.pexels.com/photos/220887/pexels-photo-220887.jpeg?auto=compress&cs=tinysrgb&w=900',
    eyebrow: 'Available 24/7',
  },
  {
    slug: 'sapa',
    name: 'Sapa (Sa Pa)',
    region: 'Northern Highlands',
    status: 'coming-soon',
    href: '/urgent-medical-care-sapa',
    blurb: '24/7 doctor call-outs in Sapa Town and the Muong Hoa Valley — a doctor '
      + 'to your hotel, homestay or Airbnb within 60 minutes for trekking illness and injuries.',
    image: 'https://images.pexels.com/photos/27629331/pexels-photo-27629331.jpeg?auto=compress&cs=tinysrgb&w=900',
    fallback: 'https://images.pexels.com/photos/39388237/pexels-photo-39388237.jpeg?auto=compress&cs=tinysrgb&w=900',
    eyebrow: 'Available 24/7',
  },
  {
    slug: 'hue',
    name: 'Hue',
    region: 'Central Coast',
    status: 'coming-soon',
    href: '/urgent-medical-care-hue',
    blurb: '24/7 urgent-care coordination with English-speaking support in Huế — '
      + 'availability and estimated arrival time confirmed for your exact address.',
    image: 'https://images.pexels.com/photos/39795315/pexels-photo-39795315.jpeg?auto=compress&cs=tinysrgb&w=900',
    fallback: 'https://images.pexels.com/photos/27536773/pexels-photo-27536773.jpeg?auto=compress&cs=tinysrgb&w=900',
    eyebrow: 'Coordinated 24/7',
  },
  {
    slug: 'lang-co',
    name: 'Lang Co',
    region: 'Central Coast',
    status: 'coming-soon',
    href: '/urgent-medical-care-lang-co',
    blurb: '24/7 urgent-care coordination with English-speaking support in Lăng Cô — '
      + 'availability and estimated arrival time confirmed for your exact address.',
    image: 'https://images.pexels.com/photos/15394107/pexels-photo-15394107.jpeg?auto=compress&cs=tinysrgb&w=900',
    fallback: 'https://images.pexels.com/photos/37981255/pexels-photo-37981255.jpeg?auto=compress&cs=tinysrgb&w=900',
    eyebrow: 'Coordinated 24/7',
  },
  {
    slug: 'dong-hoi',
    name: 'Dong Hoi',
    region: 'North Central Coast',
    status: 'coming-soon',
    href: '/urgent-medical-care-dong-hoi',
    blurb: '24/7 urgent-care coordination with English-speaking support in Đồng Hới — '
      + 'availability and estimated arrival time confirmed for your exact address.',
    image: 'https://images.unsplash.com/photo-1610610822995-752bc72117dd?crop=entropy&cs=srgb&fm=jpg&q=85&w=900',
    fallback: 'https://images.unsplash.com/photo-1588513340145-25887e90a1c8?crop=entropy&cs=srgb&fm=jpg&q=85&w=900',
    eyebrow: 'Coordinated 24/7',
  },
  {
    slug: 'phong-nha',
    name: 'Phong Nha',
    region: 'Quảng Bình',
    status: 'coming-soon',
    href: '/urgent-medical-care-phong-nha',
    blurb: '24/7 urgent-care coordination with English-speaking support in Phong Nha — '
      + 'availability and estimated arrival time confirmed for your exact address.',
    image: 'https://images.unsplash.com/photo-1686766219304-5e2fb0df9d2d?crop=entropy&cs=srgb&fm=jpg&q=85&w=900',
    fallback: 'https://images.unsplash.com/photo-1765879559673-882157dc4fa3?crop=entropy&cs=srgb&fm=jpg&q=85&w=900',
    eyebrow: 'Coordinated 24/7',
  },
];

export const getLiveUrgentCareCities = () =>
  URGENT_CARE_CITIES.filter((c) => c.status === 'live');

export const getUrgentCareCityBySlug = (slug) =>
  URGENT_CARE_CITIES.find((c) => c.slug === slug) || null;
